/* eslint-disable max-lines-per-function */
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'rankingTwoWayHorizontalBar',
  name_en: 'Ranking Two-way Horizontal Bar',
  // name_zh: '排名双向条形图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 1,
    x2: amount => amount > 1,
    y: amount => amount > 1,
    data: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    x2: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    x2: string;
    y: string;
  };
  animationDuration: number;
}

export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, hook } = api;
    const chartViewRect = $dom.getBoundingClientRect();
    const presetOption = hook.buildStandardChartPresetOption(api);
    const {
      dataIndex = 0,
      encoding: { x: leftxAttribute, x2: rightxAttribute, y },
      animationDuration: updateFrequency
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);

    // * ------------------------- data
    const x0 = rightxAttribute;
    const x1 = leftxAttribute;
    const data1 = [];
    const data2 = [];
    const nameList = [];
    data.values.forEach(el => {
      nameList.push(`${el[y]}`);
      data1.push({
        name: `${el[y]}`,
        value: +el[x0]
      });
      data2.push({
        name: `${el[y]}`,
        value: +el[x1]
      });
    });

    // * ------------------------- chart
    const containerWidth = $dom.clientWidth;
    const grid = [
      {
        dvBoxLayout: null,
        width: 0.4 * containerWidth,
        left: containerWidth * 0.4 + (containerWidth - 500) / 10 + 100,
        top: 20,
        bottom: 4
      },
      {
        dvBoxLayout: null,
        width: 0.4 * containerWidth,
        top: 20,
        left: (containerWidth - 500) / 10,
        bottom: 4
      }
    ];

    // 配置项
    return {
      animationDuration: updateFrequency,
      animationDurationUpdate: updateFrequency,
      grid,
      dataZoom: [],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: '#000000',
            opacity: 0.04
          },
          label: {
            show: false
          },
          z: 0
        },
        formatter: item => {
          const value = hook.formatDataNumberValue(item[0].value, {
            type: 'table',
            unit: dataMetaInfo[item[0].seriesName]?.unit
          });
          // prettier-ignore
          const itemObj = {
            type: util.const.SERIES_2_LEGEND_TYPE_MAP[item[0].seriesType],
            color: item[0].color,
            name: item[0].seriesName,
            value: value.value + value.unit
          }
          return hook.buildTooltip({
            config: hook.buildStandardChartPresetOption(api).tooltip,
            title: item[0]?.name,
            items: [itemObj]
          });
        }
      },
      yAxis: [
        {
          type: 'category',
          inverse: true,
          show: true,
          // name: '111',
          // max: Math.min(maxDisplayNumber, yMaxNum - 1),
          splitLine: {
            show: false
          },
          axisLine: {
            show: false
          },
          data: nameList,
          axisTick: {
            show: false
          },
          axisLabel: {
            margin: 50,
            width: 100,
            align: 'center',
            fontSize: 12,
            lineHeight: 12,
            formatter(value) {
              if (String(value).length > 5) {
                return `${value.slice(0, 5)}...`;
              }
              return value;
            }
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        },
        {
          type: 'category',
          inverse: true,
          show: true,
          gridIndex: 1,
          // max: Math.min(maxDisplayNumber, yMaxNum - 1),
          position: 'right',
          splitLine: {
            show: false
          },
          axisLine: {
            show: false
          },
          axisLabel: {
            show: false,
            fontSize: 12,
            lineHeight: 12,
            fontFamily: presetOption.textStyle.fontFamily
          },
          axisTick: {
            show: false
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        }
      ],
      xAxis: [
        {
          type: 'value',
          show: true,
          position: 'top',
          splitLine: {
            lineStyle: {
              type: 'dashed'
            }
          },
          axisLabel: {
            formatter(value) {
              const result = hook.formatDataNumberValue(value);
              if (Number(result.value >= 0)) {
                return result.value + result.unit;
              }
              return '';
            },
            dvAlignEdge: true,
            hideOverlap: true,
            showMaxLabel: true,
            showMinLabel: true,
            fontSize: 12,
            lineHeight: 12,
            fontFamily: presetOption.textStyle.fontFamily
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        },
        {
          type: 'value',
          show: true,
          position: 'top',
          gridIndex: 1,
          inverse: true,
          splitLine: {
            lineStyle: {
              type: 'dashed'
            }
          },
          axisLabel: {
            formatter(value) {
              const result = hook.formatDataNumberValue(value);
              if (Number(result.value >= 0)) {
                return result.value + result.unit;
              }
              return '';
            },
            dvAlignEdge: true,
            hideOverlap: true,
            showMaxLabel: true,
            showMinLabel: true,
            fontSize: 12,
            lineHeight: 12,
            fontFamily: presetOption.textStyle.fontFamily
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        }
      ],
      series: [
        {
          type: 'bar',
          realtimeSort: true,
          name: x0,
          data: data1,
          dvValueKey: 'value',
          itemStyle: {
            color: (presetOption?.color || ['#3366ff', '#ff9500'])[0]
          },
          label: {
            show: false,
            formatter(data) {
              if (data.data.value[0] === '') {
                return '--';
              }
              const result = hook.formatDataNumberValue(data.data.value[0], {
                type: 'table',
                unit: dataMetaInfo[x0]?.unit
              });
              return result.value + result.unit;
            }
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        },
        {
          type: 'bar',
          realtimeSort: true,
          data: data2,
          name: x1,
          dvValueKey: 'value2',
          xAxisIndex: 1,
          yAxisIndex: 1,
          itemStyle: {
            color: (presetOption?.color || ['#3366ff', '#ff9500'])[1]
          },
          label: {
            show: false,
            position: 'left',
            formatter(data) {
              if (data.data.value[0] === '') {
                return '--';
              }
              const result = hook.formatDataNumberValue(data.data.value[0], {
                type: 'table',
                unit: dataMetaInfo[x1]?.unit
              });
              return result.value + result.unit;
            }
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        }
      ],
      graphic: {
        elements: [
          {
            type: 'text',
            top: '50%',
            left: '25%',
            style: {
              font: `bold ${
                Math.min(chartViewRect.width, chartViewRect.height) / 8
              }px THSJinRongTi-Medium`,
              // fill: 'rgba(0,0,0, .1)'
              fill: 'rgba(0,0,0, 0)'
            }
          }
        ]
      }
    };
  }
});
